import { createSelector, Dispatch } from '@reduxjs/toolkit';
import {
    selectBehaviorFromBootstrap,
    selectCmsFromBootstrap,
    selectCurrencyCodeFromBootstrap,
    selectExperimentsFromBootstrap,
    selectFirstDayOfWeekFromBootstrap,
    selectLocaleFromBootstrap,
} from 'store/slice/bootstrap';
import { Weekday } from 'core-framework/util/datetime/Weekday';
import { addDays, addYears } from 'date-fns';
import {
    selectActivityIdFromSelectedOffer,
    selectAvailabilityIsLoading,
    selectAvailabilitySelectedDate,
    selectBasedCalendarOfferIds,
    selectOfferGroupIdFromSelectedOfferGroup,
    selectOfferLevelSelectedDate,
    selectOfferOptionTokenFromOfferSelected,
    selectPossibleOfferOptions,
    selectUnavailableDatesFromAvailabilityResponse,
} from 'component/page/detail/store/availabilitySelectorIndex';
import {
    selectAdditionalCalendarLoading,
    selectCalendarState,
    selectFetchedMonths,
    selectOfferLevelCalendarsFromCalendarState,
    selectSchedulesFromCalendarState,
} from 'component/page/detail/store/calendar.selectors';
import { AvailabilityCalendar } from 'gql/autogen/AvailabilityCalendar';
import { getTodayDate } from 'core-framework/util/datetime/date';
import { fetchAvailability, resetSelectedOfferCard, setOfferNotification, setOfferOptionSelectedDate, setSelectedDate } from 'component/page/detail/store/availabilityIndex';
import { setTravelDateInSearchBoxSettingsState } from 'store/slice/home';
import { Label } from '@drone-js/date';
import { fetchCalendarPrice } from 'component/page/detail/store/calendar';
import { selectActivityTokenFromDetailState } from 'component/page/detail/store/detail.selectors';
import { OfferNotificationType } from 'component/page/detail/store/availabilityIndex.types';
import { filterUnavailableDates, getCheapestSchedules, mapPriceLabels } from 'component/page/detail/PopoverCalendar/PopoverCalendarUtils';
import { CmsValue } from 'core/type/BootstrapConfig.types';
import { ActivitiesBehavior } from 'core/type/behaviors.types';
import { ActivityOfferToken } from 'core/type/Details.types';
import { ExperimentId } from 'core/type/ExperimentId';
import { isB } from 'core/window/bootstrapConfig';

export interface OwnProps {
    onSelectDateCallback?: () => void;
    isOfferLevel?: boolean;
    isPopover?: boolean;
}

interface DispatchProps {
    setSelectedDate: (selectedDate: Date, offerToken: ActivityOfferToken, isOfferLevel: boolean, offerId: number, shouldFetchAvailability: boolean) => void;
    resetSelectedOfferCard: () => void;
    fetchCalendarMonth: (date: Date, activityToken: string, fetchedMonths?: Date[]) => void;
}

export interface StateProps {
    latestPossibleDate: Date;
    todayDate: Date;
    availabilitySelectedDate: Date;
    offerLevelSelectedDate: Date;
    availabilityCalendar?: AvailabilityCalendar;
    priceLabels?: Label[];
    offerLevelPriceLabels?: Label[];
    isLoading?: boolean;
    fetchedMonths?: Date[];
    activityToken: string;
    offerOptionToken: string;
    selectedOfferId: number;
    unavailableDates: Date[];
    offerLevelUnavailableDates: Date[];
    isAvailabilityLoading: boolean;
    basedCalendarOfferIds: (number | undefined)[];
    cms: CmsValue;
    firstDayOfWeek?: Weekday;
    behaviors: ActivitiesBehavior;
    currency: string;
    locale: string;
    activityId: number | undefined;
    isHidePriceInCalendar: boolean;
}

export type Props = OwnProps & StateProps & DispatchProps;

const todayDate = getTodayDate();
const latestPossibleDate = addDays(addYears(todayDate, 1), -1); // it will become year+1 because it also counts today and gql will break if user select that date so need to minus 1

export const mapStateToProps = createSelector(
    selectFirstDayOfWeekFromBootstrap,
    selectExperimentsFromBootstrap,
    selectAvailabilitySelectedDate,
    selectOfferLevelSelectedDate,
    selectCalendarState,
    selectAdditionalCalendarLoading,
    selectFetchedMonths,
    selectLocaleFromBootstrap,
    selectActivityTokenFromDetailState,
    selectOfferOptionTokenFromOfferSelected,
    selectOfferGroupIdFromSelectedOfferGroup,
    selectOfferLevelCalendarsFromCalendarState,
    selectSchedulesFromCalendarState,
    selectPossibleOfferOptions,
    selectAvailabilityIsLoading,
    selectBasedCalendarOfferIds,
    selectUnavailableDatesFromAvailabilityResponse,
    selectCmsFromBootstrap,
    selectBehaviorFromBootstrap,
    selectCurrencyCodeFromBootstrap,
    selectActivityIdFromSelectedOffer,
    (
        firstDayOfWeek,
        experiments,
        availabilitySelectedDate,
        offerLevelSelectedDate,
        state,
        isLoading,
        fetchedMonths,
        locale,
        activityToken,
        offerOptionToken,
        selectedOfferId,
        offerCalendars,
        schedules,
        possibleOffers,
        isAvailabilityLoading,
        basedCalendarOfferIds,
        unavailableDatesFromAvailabilityResponse,
        cms,
        behaviors,
        currency,
        activityId,
    ): StateProps => {
        const cheapestSchedules = getCheapestSchedules(possibleOffers, schedules, offerCalendars, basedCalendarOfferIds);

        const priceLabels: Label[] = mapPriceLabels(schedules, locale);
        const offerLevelPriceLabels: Label[] = mapPriceLabels(cheapestSchedules, locale);

        const unavailableDates = filterUnavailableDates(schedules);
        const offerLevelUnavailableDates = [...filterUnavailableDates(cheapestSchedules), ...unavailableDatesFromAvailabilityResponse];

        return {
            activityToken,
            offerOptionToken,
            firstDayOfWeek,
            latestPossibleDate,
            todayDate,
            offerLevelSelectedDate,
            availabilitySelectedDate,
            priceLabels,
            isLoading,
            fetchedMonths,
            selectedOfferId,
            offerLevelPriceLabels,
            unavailableDates,
            offerLevelUnavailableDates,
            isAvailabilityLoading,
            basedCalendarOfferIds,
            cms,
            availabilityCalendar: state.calendarResponse?.result?.calendar,
            behaviors,
            currency,
            locale,
            activityId,
            isHidePriceInCalendar: isB(ExperimentId.ACTD_5, experiments),
        };
    },
);

export const mapDispatchToProps = (dispatch: Dispatch): DispatchProps => ({
    setSelectedDate: (date: Date, offerToken: ActivityOfferToken, isOfferLevel: boolean, offerId: number, shouldFetchAvailability: boolean) => {
        if (isOfferLevel) {
            dispatch(setOfferOptionSelectedDate({ offerId, selectedDate: date }));
            dispatch(setOfferNotification({ [OfferNotificationType.DateChip]: false }));
        } else {
            dispatch(setSelectedDate({ selectedDate: date }));
            dispatch(setTravelDateInSearchBoxSettingsState(date));
        }
        if (shouldFetchAvailability) {
            fetchAvailability(offerToken.activityToken, { excludePassengers: false, enableOfferFilter: offerToken.enableOfferFilter });
        }
    },
    resetSelectedOfferCard: () => {
        dispatch(resetSelectedOfferCard());
    },
    fetchCalendarMonth: (date: Date, activityToken: string, fetchedMonths?: Date[]) => {
        const isMonthLoaded = fetchedMonths?.some((item) => item.getMonth() === date.getMonth() && item.getFullYear() === date.getFullYear());
        if (!isMonthLoaded) fetchCalendarPrice(date, activityToken);
    },
});
